/**
 * Drawer para solicitar + desembolsar un crédito emergente.
 *
 * Flujo:
 *   1. Operador elige monto (Q300–Q1,000) y plazo (15 o 30 días)
 *   2. El drawer muestra el plan calculado en vivo (RPC simular_credito)
 *   3. Al confirmar:
 *      a. Llama solicitar_credito_admin → solicitud auto-aprobada
 *      b. Llama desembolsar_credito_admin → genera crédito + abona a la
 *         cuenta de ahorro corriente del asociado
 *   4. Muestra vista de éxito con el plan + saldo nuevo
 *
 * Lectura del tipo y bandas de mora desde el catálogo. Si más adelante
 * agregamos otro tipo de crédito, este mismo drawer se puede usar
 * cambiando el código del tipo.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, Printer, Wallet, X } from '@phosphor-icons/react'
import { DrawerShell } from './DrawerShell'
import { supabase } from '@/lib/supabase'
import { imprimirPagareCredito } from '@/lib/imprimirPagareCredito'

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  nombreCompleto: string
  noAsociado: string
  onSuccess: () => void
  /** Código del tipo de crédito a originar. Default: emergente. */
  tipoCodigo?: string
}

type TipoCredito = {
  id: string
  codigo: string
  nombre: string
  monto_minimo: number
  monto_maximo: number
  plazos_dias: number[]
  tasa: number
  periodicidad_tasa: string
  aprobacion_automatica: boolean
}

type PlanCuota = {
  numero: number
  fecha: string
  capital: number
  interes: number
  total: number
  saldo_post: number
}

type Plan = {
  num_cuotas: number
  fecha_primera_cuota: string
  fecha_ultima_cuota: string
  total_capital: number
  total_interes: number
  total_a_pagar: number
  cuotas: PlanCuota[]
}

type DesembolsoResult = {
  credito_id: string
  plan: Plan
  cuenta_ahorro_id: string
  saldo_cuenta_nuevo: number
}

/**
 * Traduce errores crudos de Postgres (raise exception de las RPCs) a
 * mensajes en lenguaje humano para el operador.
 */
function traducirError(raw: string): string {
  const s = raw.toLowerCase()
  if (s.includes('ya tiene') && s.includes('credito')) {
    return 'Este asociado ya tiene un crédito emergente vigente. Hay que cancelar el anterior antes de pedir uno nuevo.'
  }
  if (s.includes('ya hay una solicitud')) {
    return 'Ya existe una solicitud de crédito en proceso para este asociado.'
  }
  if (s.includes('sin cuenta de ahorro corriente activa')) {
    return 'El asociado no tiene cuenta de ahorro corriente activa. Hay que registrar la inscripción antes.'
  }
  if (s.includes('debe estar activo')) {
    return 'El asociado no está en estado activo — no puede recibir crédito.'
  }
  if (s.includes('antiguedad insuficiente')) {
    return 'El asociado no tiene la antigüedad mínima requerida por este producto.'
  }
  if (s.includes('monto') && s.includes('fuera de rango')) {
    return raw // ya viene formateado con los topes
  }
  if (s.includes('plazo') && s.includes('no permitido')) {
    return raw // ya viene legible
  }
  if (s.includes('tipo de credito') && s.includes('no encontrado')) {
    return 'El producto de crédito no está disponible. Avisá al admin del sistema.'
  }
  if (s.includes('solo admin')) {
    return 'No tenés permisos para crear créditos. Hablá con el admin.'
  }
  // Default: devolver el mensaje original
  return raw
}

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtFecha = (iso: string) => {
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

type CreditoVigente = {
  id: string
  tipoNombre: string
  saldoCapital: number
  proximaCuotaMonto: number
  proximaCuotaFecha: string | null
}

export function SolicitarCreditoDrawer({
  open, onClose, asociadoId, nombreCompleto, noAsociado, onSuccess,
  tipoCodigo = 'emergente_quincenal',
}: Props) {
  const navigate = useNavigate()
  const [tipo, setTipo] = useState<TipoCredito | null>(null)
  // Solicitud creada para tipos de aprobación MANUAL (no se desembolsa acá;
  // el asociado firma el pagaré en la app y luego se desembolsa).
  const [solicitudManual, setSolicitudManual] = useState<{ plan: Plan } | null>(null)
  const [bloqueoVigente, setBloqueoVigente] = useState<CreditoVigente | null>(null)
  const [monto, setMonto] = useState<number>(500)
  const [plazo, setPlazo] = useState<number>(15)
  const [proposito, setProposito] = useState('')

  const [plan, setPlan] = useState<Plan | null>(null)
  const [planError, setPlanError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<DesembolsoResult | null>(null)

  // Cargar el tipo al abrir + chequear si ya hay crédito vigente
  useEffect(() => {
    if (!open) return
    setSuccess(null)
    setSolicitudManual(null)
    setError(null)
    setBloqueoVigente(null)
    void (async () => {
      // 1. Cargar el tipo
      const { data: tipoData, error: tipoErr } = await supabase
        .from('tipo_credito')
        .select('id, codigo, nombre, monto_minimo, monto_maximo, plazos_dias, tasa, periodicidad_tasa, aprobacion_automatica')
        .eq('codigo', tipoCodigo)
        .single()
      if (tipoErr) { setError(tipoErr.message); return }
      const t = tipoData as unknown as TipoCredito
      setTipo(t)

      // 2. Chequear bloqueo por crédito vigente (operaciones_simultaneas_max = 1)
      const { data: vigente } = await supabase
        .from('credito')
        .select(`
          id, saldo_capital,
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado
          )
        `)
        .eq('asociado_id', asociadoId)
        .eq('tipo_credito_id', t.id)
        .in('estado', ['vigente', 'en_mora', 'al_dia'])
        .limit(1)
        .maybeSingle()

      if (vigente) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const v = vigente as any
        const cuotasPend = (v.cuotas ?? [])
          .filter((q: { estado: string }) => q.estado !== 'pagada' && q.estado !== 'castigada')
          .sort((a: { numero: number }, b: { numero: number }) => a.numero - b.numero)
        const prox = cuotasPend[0]
        const proxMonto = prox
          ? (Number(prox.capital) - Number(prox.capital_pagado)) +
            (Number(prox.interes) - Number(prox.interes_pagado)) +
            (Number(prox.mora_acumulada) - Number(prox.mora_pagada))
          : 0
        setBloqueoVigente({
          id: v.id,
          tipoNombre: v.tipo?.nombre ?? t.nombre,
          saldoCapital: Number(v.saldo_capital),
          proximaCuotaMonto: proxMonto,
          proximaCuotaFecha: prox?.fecha_vencimiento ?? null,
        })
        return
      }

      // Defaults del formulario
      const mid = Math.min(t.monto_maximo, Math.max(t.monto_minimo, 500))
      setMonto(mid)
      setPlazo(t.plazos_dias[0] ?? 15)
    })()
  }, [open, asociadoId, tipoCodigo])

  // Simular plan en vivo cuando cambian monto/plazo
  useEffect(() => {
    if (!tipo) return
    if (monto < tipo.monto_minimo || monto > tipo.monto_maximo) {
      setPlan(null)
      setPlanError(`Monto debe estar entre ${fmtQ(tipo.monto_minimo)} y ${fmtQ(tipo.monto_maximo)}`)
      return
    }
    setPlanError(null)
    let cancel = false
    void (async () => {
      const { data, error } = await supabase.rpc('simular_credito', {
        p_tipo_credito_id: tipo.id,
        p_monto: monto,
        p_plazo_dias: plazo,
      })
      if (cancel) return
      if (error) { setPlanError(error.message); setPlan(null); return }
      setPlan(data as Plan)
    })()
    return () => { cancel = true }
  }, [tipo, monto, plazo])

  const tasaPct = useMemo(
    () => (tipo ? (tipo.tasa * 100).toFixed(2) + '% ' + tipo.periodicidad_tasa : '—'),
    [tipo],
  )

  if (!open) return null

  // -------- Vista BLOQUEO: ya tiene crédito vigente --------
  if (bloqueoVigente) {
    return (
      <DrawerShell onClose={onClose}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: 'var(--warning, #d97706)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 800,
            }}>!</div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Ya tiene un crédito abierto</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
                {nombreCompleto} · {noAsociado}
              </p>
            </div>
            <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--muted-foreground)' }}>
              <X size={20} />
            </button>
          </div>

          <p style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.55, margin: 0 }}>
            El producto <strong>{bloqueoVigente.tipoNombre}</strong> permite{' '}
            <strong>solo 1 crédito vigente por asociado</strong>. Para abrir uno nuevo, primero hay
            que cancelar el crédito actual.
          </p>

          {/* Card del crédito vigente */}
          <div
            style={{
              borderRadius: 16, overflow: 'hidden',
              background: 'var(--surface)', border: '1px solid var(--hairline)',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <div style={{
              background: 'var(--brand)', color: '#fff',
              padding: '10px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {bloqueoVigente.tipoNombre}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                background: 'rgba(255,255,255,0.20)', textTransform: 'uppercase', letterSpacing: 0.8,
              }}>
                ● Vigente
              </span>
            </div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p style={{
                margin: 0, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)',
                textTransform: 'uppercase', letterSpacing: 1.5,
              }}>
                Saldo capital
              </p>
              <p style={{
                margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--ink)',
                fontFeatureSettings: '"tnum"', letterSpacing: '-0.01em',
              }}>
                {fmtQ(bloqueoVigente.saldoCapital)}
              </p>
              {bloqueoVigente.proximaCuotaMonto > 0 && (
                <div style={{
                  marginTop: 8, paddingTop: 10,
                  borderTop: '1px dashed var(--hairline)',
                  display: 'flex', justifyContent: 'space-between', fontSize: 12,
                }}>
                  <span style={{ color: 'var(--ink-soft)' }}>Próxima cuota</span>
                  <strong style={{ color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
                    {fmtQ(bloqueoVigente.proximaCuotaMonto)}
                    {bloqueoVigente.proximaCuotaFecha && (
                      <> · {fmtFecha(bloqueoVigente.proximaCuotaFecha)}</>
                    )}
                  </strong>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
            <button
              className="btn btn-ghost"
              onClick={onClose}
              style={{ flex: 1 }}
            >
              Cerrar
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                onClose()
                navigate(`/neocore/creditos/${bloqueoVigente.id}`)
              }}
              style={{ flex: 1 }}
            >
              Ver crédito actual →
            </button>
          </div>
        </div>
      </DrawerShell>
    )
  }

  const onConfirmar = async () => {
    if (!tipo || !plan) return
    setSubmitting(true)
    setError(null)
    try {
      // 1) Solicitar
      const { data: sol, error: solErr } = await supabase.rpc('solicitar_credito_admin', {
        p_input: {
          asociado_id: asociadoId,
          tipo_credito_codigo: tipo.codigo,
          monto,
          plazo_dias: plazo,
          proposito: proposito.trim() || null,
        },
      })
      if (solErr) throw solErr
      const sd = sol as { solicitud_id: string; estado: string }

      if (tipo.aprobacion_automatica) {
        // Flujo AUTO (ej. emergente): se aprueba sola y se desembolsa de una.
        if (sd.estado !== 'aprobada') {
          throw new Error('La solicitud quedó pendiente — requiere revisión manual')
        }
        const { data: des, error: desErr } = await supabase.rpc('desembolsar_credito_admin', {
          p_input: { solicitud_id: sd.solicitud_id },
        })
        if (desErr) throw desErr
        setSuccess(des as DesembolsoResult)
      } else {
        // Flujo MANUAL (ej. consumo): la dejamos aprobada, SIN desembolsar.
        // El asociado firma el pagaré en la app; luego se desembolsa desde
        // la bandeja de originación.
        if (sd.estado !== 'aprobada') {
          const { error: apErr } = await supabase.rpc('aprobar_solicitud_credito_admin', {
            p_solicitud_id: sd.solicitud_id,
            p_nota: 'Originado desde NeoCore',
          })
          if (apErr) throw apErr
        }
        setSolicitudManual({ plan: plan })
      }
      onSuccess()
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Error desconocido'
      setError(traducirError(raw))
    } finally {
      setSubmitting(false)
    }
  }

  // -------- Vista de éxito MANUAL (consumo): solicitud lista para firma --------
  if (solicitudManual) {
    return (
      <DrawerShell onClose={onClose}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle size={28} weight="duotone" color="var(--ok-ink, #0f8a57)" />
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Solicitud creada · lista para firma</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
                {nombreCompleto} · {noAsociado}
              </p>
            </div>
          </div>

          <div style={{
            border: '1px solid rgba(217,119,6,0.30)', background: 'rgba(217,119,6,0.08)',
            borderRadius: 10, padding: '14px 16px', fontSize: 13, color: 'var(--warning, #d97706)', lineHeight: 1.5,
          }}>
            <strong>Falta la firma del asociado.</strong> Va a ver el crédito en la app y firmar el pagaré.
            Cuando firme, lo desembolsás desde <strong>Originación de créditos</strong>. No se desembolsó todavía.
          </div>

          <div style={{ background: 'var(--bg, #fbf7f3)', borderRadius: 12, padding: 14, border: '1px solid var(--line)' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 4px' }}>
              Cuota · {solicitudManual.plan.num_cuotas} cuota{solicitudManual.plan.num_cuotas > 1 ? 's' : ''}
            </p>
            <p style={{ fontSize: 24, fontWeight: 800, margin: 0, fontFeatureSettings: '"tnum"' }}>
              {fmtQ(solicitudManual.plan.cuotas[0]?.total ?? 0)} <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted-foreground)' }}>/ cuota</span>
            </p>
            <div style={{ display: 'flex', gap: 12, fontSize: 12, marginTop: 6, color: 'var(--ink-soft)' }}>
              <span>Total <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(solicitudManual.plan.total_a_pagar)}</strong></span>
              <span>·</span>
              <span>Intereses <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(solicitudManual.plan.total_interes)}</strong></span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
            <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cerrar</button>
            <button
              className="btn btn-primary"
              onClick={() => { onClose(); navigate('/neocore/creditos/originacion') }}
              style={{ flex: 1 }}
            >
              Ver originación →
            </button>
          </div>
        </div>
      </DrawerShell>
    )
  }

  // -------- Vista de éxito --------
  if (success) {
    return (
      <DrawerShell onClose={onClose}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle size={28} weight="duotone" color="var(--ok-ink, #0f8a57)" />
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Crédito desembolsado</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
                {nombreCompleto} · {noAsociado}
              </p>
            </div>
          </div>

          <div style={{
            background: 'var(--brand-soft, #ffe4d4)', borderRadius: 12, padding: 16,
          }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--brand-deep, #c43c00)', textTransform: 'uppercase' }}>
              Saldo nuevo en cuenta de ahorro
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 28, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
              {fmtQ(success.saldo_cuenta_nuevo)}
            </p>
          </div>

          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>Plan de pagos</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 0', fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 0', fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>Vence</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>Capital</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>Interés</th>
                  <th style={{ textAlign: 'right', padding: '6px 0', fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {success.plan.cuotas.map((c) => (
                  <tr key={c.numero} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={{ padding: '8px 0' }}>{c.numero}</td>
                    <td style={{ padding: '8px 0' }}>{fmtFecha(c.fecha)}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFeatureSettings: '"tnum"' }}>{fmtQ(c.capital)}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFeatureSettings: '"tnum"', color: 'var(--brand-deep)' }}>{fmtQ(c.interes)}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', fontFeatureSettings: '"tnum"', fontWeight: 700 }}>{fmtQ(c.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} style={{ padding: '10px 0 6px', fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                    Total a pagar
                  </td>
                  <td style={{ padding: '10px 0 6px', textAlign: 'right', fontFeatureSettings: '"tnum"', fontWeight: 800, fontSize: 15 }}>
                    {fmtQ(success.plan.total_a_pagar)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
            <button
              className="btn btn-primary"
              onClick={async () => {
                const r = await imprimirPagareCredito(success.credito_id)
                if (!r.ok) alert('No pudimos imprimir el pagaré: ' + r.error)
              }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Printer size={14} weight="bold" /> Imprimir pagaré
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-ghost"
                onClick={onClose}
                style={{ flex: 1 }}
              >
                Cerrar
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  onClose()
                  navigate(`/neocore/creditos/${success.credito_id}`)
                }}
                style={{ flex: 1 }}
              >
                Ver detalle
              </button>
            </div>
          </div>
        </div>
      </DrawerShell>
    )
  }

  // -------- Vista del formulario --------
  return (
    <DrawerShell onClose={onClose}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Wallet size={26} weight="duotone" color="var(--brand)" />
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{tipo?.nombre ?? 'Nuevo crédito'}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {nombreCompleto} · {noAsociado}
            </p>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--muted-foreground)' }}>
            <X size={20} />
          </button>
        </div>

        {!tipo && <p style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Cargando producto…</p>}

        {tipo && (
          <>
            {/* Monto */}
            <div>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  Monto del crédito
                </span>
                <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand)', fontFeatureSettings: '"tnum"' }}>
                  {fmtQ(monto)}
                </span>
              </label>
              <input
                type="range"
                min={tipo.monto_minimo}
                max={tipo.monto_maximo}
                step={50}
                value={monto}
                onChange={(e) => setMonto(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--brand)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted-foreground)', fontFeatureSettings: '"tnum"', marginTop: 2 }}>
                <span>{fmtQ(tipo.monto_minimo)}</span>
                <span>{fmtQ(tipo.monto_maximo)}</span>
              </div>
            </div>

            {/* Plazo */}
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 8px' }}>
                Plazo
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tipo.plazos_dias.length}, 1fr)`, gap: 8 }}>
                {tipo.plazos_dias.map((d) => (
                  <button
                    key={d}
                    onClick={() => setPlazo(d)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: plazo === d ? '2px solid var(--brand)' : '1px solid var(--line)',
                      background: plazo === d ? 'var(--brand-soft, #ffe4d4)' : '#fff',
                      color: plazo === d ? 'var(--brand-deep)' : 'var(--ink)',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {tipo.periodicidad_tasa === 'mensual' ? `${Math.round(d / 30)} meses` : `${d} días`}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '6px 0 0' }}>Tasa {tasaPct} sobre saldos.</p>
            </div>

            {/* Propósito */}
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 6px' }}>
                Propósito (opcional)
              </p>
              <input
                type="text"
                value={proposito}
                onChange={(e) => setProposito(e.target.value)}
                placeholder="ej. Reparación de moto, gastos médicos…"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--line)', fontSize: 13, fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Plan calculado en vivo */}
            <div style={{
              background: 'var(--bg, #fbf7f3)', borderRadius: 12, padding: 14,
              border: '1px solid var(--line)',
            }}>
              {planError && <p style={{ fontSize: 12, color: 'var(--danger, #a32d2d)', margin: 0 }}>{planError}</p>}
              {plan && (
                <>
                  <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 4px' }}>
                    Total a pagar · {plan.num_cuotas} cuota{plan.num_cuotas > 1 ? 's' : ''}
                  </p>
                  <p style={{ fontSize: 24, fontWeight: 800, margin: 0, fontFeatureSettings: '"tnum"' }}>
                    {fmtQ(plan.total_a_pagar)}
                  </p>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, marginTop: 6, color: 'var(--ink-soft, #4b5563)' }}>
                    <span>Intereses <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(plan.total_interes)}</strong></span>
                    <span>·</span>
                    <span>Vence <strong>{fmtFecha(plan.fecha_ultima_cuota)}</strong></span>
                  </div>
                </>
              )}
            </div>

            {error && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(163,45,45,0.08)', color: 'var(--danger, #a32d2d)', fontSize: 13 }}>
                {error}
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={onConfirmar}
              disabled={submitting || !plan}
              style={{ marginTop: 'auto' }}
            >
              {submitting
                ? (tipo?.aprobacion_automatica ? 'Desembolsando…' : 'Creando solicitud…')
                : (tipo?.aprobacion_automatica
                    ? `Aprobar y desembolsar ${fmtQ(monto)}`
                    : `Crear solicitud · ${fmtQ(monto)} (a firmar)`)}
            </button>
          </>
        )}
      </div>
    </DrawerShell>
  )
}
